import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { buildHostClasses } from '../../../base/class-builder';
import { HxBaseControl } from '../../../base/hx-base-control';
import { HxOptionGroup } from '../shared/option-group.model';
import { HxOption } from '../shared/option.model';
import { HxSelectSize } from '../shared/select-types';

/**
 * A native HTML select component that leverages browser-native styling for simple single-value selection. Implements
 * ControlValueAccessor for seamless Angular Forms integration.
 *
 * @example
 *   ```html
 *   <hx-native-select
 *     [options]="countries"
 *     [size]="'md'"
 *     [placeholder]="'Select a country'"
 *     (valueChange)="onCountryChange($event)">
 *   </hx-native-select>
 *   ```;
 *
 * @component HxNativeSelect
 * @implements ControlValueAccessor
 */
@Component({
  selector: 'hx-native-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './native-select.component.html',
  styleUrls: [
    './native-select.component.css',
    '../select-tokens.css',
    './native-select-tokens.css',
    '../../../styles/form-control-tokens.css',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': 'hostClasses()',
    '[attr.data-size]': 'size()',
    '[attr.data-error]': 'error()',
    '[attr.data-disabled]': 'disabled()',
    '[attr.data-readonly]': 'readonly()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HxNativeSelectComponent),
      multi: true,
    },
  ],
})
export class HxNativeSelectComponent<T = any> extends HxBaseControl implements ControlValueAccessor {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);

  // ============================================================================
  // INPUTS - Component Configuration
  // ============================================================================

  /** Array of options or option groups to display */
  readonly options = input<(HxOption<T> | HxOptionGroup<T>)[]>([]);

  /**
   * Component size variant
   *
   * @default 'md'
   */
  readonly size = input<Exclude<HxSelectSize, 'xl'>>('md');

  /**
   * Whether the select is in error state
   *
   * @default false
   */
  readonly error = input<boolean>(false);

  /**
   * Placeholder text shown when no value is selected
   *
   * @default 'Select an option'
   */
  readonly placeholder = input<string>('Select an option');

  /** ARIA label for accessibility */
  override readonly ariaLabel = input<string | undefined>(undefined, { alias: 'aria-label' });

  /** ID of element that labels this select */
  readonly ariaLabelledBy = input<string | undefined>(undefined, { alias: 'aria-labelledby' });

  /** ID of element that describes this select */
  readonly ariaDescribedBy = input<string | undefined>(undefined, { alias: 'aria-describedby' });

  /** Custom CSS classes for the wrapper element */
  readonly customClass = input<string>('');

  // ============================================================================
  // MODEL - Two-way binding
  // ============================================================================

  /** Current value of the select (supports two-way binding) */
  readonly value = model<T | null>(null);

  // ============================================================================
  // OUTPUTS - Event Emitters
  // ============================================================================

  /** Emitted when the select receives focus */
  readonly focused = output<FocusEvent>();

  /** Emitted when the select loses focus */
  readonly blurred = output<FocusEvent>();

  // ============================================================================
  // VIEW CHILDREN
  // ============================================================================

  /** Reference to the native select element */
  private readonly selectElement = viewChild<ElementRef<HTMLSelectElement>>('selectRef');

  /** Whether the select currently has focus */
  protected readonly isFocused = signal<boolean>(false);

  /** Flattened array of all options (including from groups) */
  protected readonly flatOptions = computed(() => {
    const opts = this.options();
    const flat: HxOption<T>[] = [];

    opts.forEach((item) => {
      if ('options' in item) {
        // It's an option group
        flat.push(...(item as HxOptionGroup<T>).options);
      } else {
        // It's a single option
        flat.push(item as HxOption<T>);
      }
    });

    return flat;
  });

  /** Whether a value is currently selected */
  protected readonly hasValue = computed(() => {
    return this.value() !== null && this.value() !== undefined;
  });

  /** Computed CSS classes for the host element */
  protected readonly hostClasses = buildHostClasses({
    baseClass: 'hx-native-select',
    size: this.size,
    error: this.error,
    disabled: this.disabled,
    readonly: this.readonly,
    active: this.isFocused,
    customClass: this.customClass,
    additionalStates: {
      'hx-native-select--has-value': this.hasValue,
    },
  });

  // ============================================================================
  // CONTROL VALUE ACCESSOR
  // ============================================================================

  private onChange: (value: T | null) => void = () => {};
  private onTouched: () => void = () => {};

  /** Writes a new value to the select (from Forms API) */
  override writeValue(value: T | null): void {
    this.value.set(value);
  }

  /** Registers the onChange callback */
  override registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  /** Registers the onTouched callback */
  override registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** Sets the disabled state */
  override setDisabledState(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /** Handles native select change events */
  protected onNativeChange(event: Event): void {
    if (this.disabledState() || this.readonly()) {
      return;
    }

    const selectEl = event.target as HTMLSelectElement;
    const selectedIndex = selectEl.selectedIndex;

    // Skip if placeholder is selected (index 0)
    if (selectedIndex === 0 && this.placeholder()) {
      this.updateValue(null);
      return;
    }

    // Find the selected option by index
    const flatOpts = this.flatOptions();
    const adjustedIndex = this.placeholder() ? selectedIndex - 1 : selectedIndex;

    if (adjustedIndex >= 0 && adjustedIndex < flatOpts.length) {
      const selectedOption = flatOpts[adjustedIndex];
      if (selectedOption) {
        this.updateValue(selectedOption.value);
      }
    }
  }

  /** Handles focus events */
  protected onFocus(event: FocusEvent): void {
    this.isFocused.set(true);
    this.focused.emit(event);
  }

  /** Handles blur events */
  protected onBlur(event: FocusEvent): void {
    this.isFocused.set(false);
    this.onTouched();
    this.blurred.emit(event);
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /** Programmatically focus the select element */
  focus(): void {
    const el = this.selectElement();
    if (el) {
      el.nativeElement.focus();
    }
  }

  /** Programmatically blur the select element */
  blur(): void {
    const el = this.selectElement();
    if (el) {
      el.nativeElement.blur();
    }
  }

  /** Get the current value */
  getValue(): T | null {
    return this.value();
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /** Updates the internal value and notifies Forms API */
  private updateValue(newValue: T | null): void {
    this.value.set(newValue);
    this.onChange(newValue);
  }

  /** Helper to check if an item is an option group */
  protected isOptionGroup(item: HxOption<T> | HxOptionGroup<T>): item is HxOptionGroup<T> {
    return 'options' in item && Array.isArray((item as HxOptionGroup<T>).options);
  }
}
